import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ExternalLink, History, MapPin } from 'lucide-react';
import { PERMISSIONS } from '@pms/shared';
import { dmTeamService } from '@/services/dm-team.service';
import type { DmActivity, DmActivityHistory } from '@/services/dm-team.service';
import { usePermission } from '@/hooks/usePermission';
import { DataTable } from '@/components/ui/data-table';
import type { Column } from '@/components/ui/data-table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { isHttpUrl } from './uploadLink';

type StorageTab = 'all' | 'uploaded' | 'cancelled';

const STORAGE_EMPTY_TEXT: Record<StorageTab, string> = {
  all:       'No items in storage yet',
  uploaded:  'Nothing uploaded yet',
  cancelled: 'No cancelled activities',
};

// ── Activity History Dialog ───────────────────────────────────────────────────

const ACTION_COLORS: Record<string, string> = {
  created:          'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  updated:          'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-400',
  status_changed:   'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  assigned:         'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  moved_to_storage: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400',
  uploaded:         'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-400',
  scheduled:        'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  deleted:          'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
};

const ACTION_BORDER: Record<string, string> = {
  created:          'border-l-green-400 dark:border-l-green-600',
  updated:          'border-l-violet-400 dark:border-l-violet-600',
  status_changed:   'border-l-purple-400 dark:border-l-purple-600',
  assigned:         'border-l-amber-400 dark:border-l-amber-600',
  moved_to_storage: 'border-l-teal-400 dark:border-l-teal-600',
  uploaded:         'border-l-violet-400 dark:border-l-violet-600',
  scheduled:        'border-l-orange-400 dark:border-l-orange-600',
  deleted:          'border-l-red-400 dark:border-l-red-600',
};

const FIELD_DISPLAY: Record<string, string> = {
  title: 'Title', shooting_category_id: 'Category', location: 'Location',
  date_time: 'Date & Time', page_id: 'Page', updated_page_id: 'Updated Page', status: 'Status',
};

function ActivityHistoryDialog({
  activityId, title, open, onClose,
}: { activityId: string; title: string; open: boolean; onClose: () => void }) {
  const { data } = useQuery({
    queryKey: ['dm-team', 'activity-history', activityId],
    queryFn: () => dmTeamService.getActivityHistory(activityId),
    enabled: open,
  });

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>History — {title}</DialogTitle></DialogHeader>
        <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
          {(data?.data ?? []).length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-6">No history yet</p>
          )}
          {(data?.data ?? []).map((h: DmActivityHistory) => {
            const assignedVal = h.action_type === 'assigned' && h.new_value !== null
              ? (h.new_value as any) : null;
            const assignedUsers: string[] = Array.isArray(assignedVal?.users) ? assignedVal.users : [];
            const assignedPage: string | null = assignedVal?.page ?? null;
            const hasAssignedUsers = assignedUsers.length > 0;
            const hasFieldChange = !!(h.field_name && (h.old_value !== null || h.new_value !== null));
            const showDescription = !!(h.description && !hasAssignedUsers && h.action_type !== 'status_changed' && !hasFieldChange);

            return (
              <div key={h.id} className={`rounded-lg border border-l-4 ${ACTION_BORDER[h.action_type] ?? 'border-l-gray-300'} px-4 py-3`}>
                {/* Header: badge + timestamp + actor */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full shrink-0 ${ACTION_COLORS[h.action_type] ?? 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'}`}>
                    {h.action_type.replace(/_/g, ' ')}
                  </span>
                  <span className="text-xs text-muted-foreground">{format(new Date(h.created_at), 'd MMM yyyy, HH:mm')}</span>
                  <span className="text-xs font-semibold ml-auto shrink-0">{h.created_by_name}</span>
                </div>

                {/* Plain description */}
                {showDescription && (
                  <p className="text-sm text-muted-foreground mt-2">{h.description}</p>
                )}

                {/* Assigned users */}
                {hasAssignedUsers && (
                  <div className="mt-2.5 space-y-2">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Assigned to</p>
                    <div className="flex flex-wrap gap-1.5">
                      {assignedUsers.map((name: string) => (
                        <span key={name} className="text-xs px-2.5 py-1 rounded-full bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:text-amber-400 font-medium border border-amber-200 dark:border-amber-800">
                          {name}
                        </span>
                      ))}
                    </div>
                    {assignedPage && (
                      <p className="text-xs text-muted-foreground">
                        Page section: <span className="font-semibold text-foreground">{assignedPage}</span>
                      </p>
                    )}
                  </div>
                )}

                {/* Field change: label above, old → new pills */}
                {hasFieldChange && (
                  <div className="mt-2.5 space-y-1.5">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      {FIELD_DISPLAY[h.field_name!] ?? h.field_name}
                    </p>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-sm px-2.5 py-1 rounded-md font-medium ${h.old_value !== null ? 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400 line-through' : 'text-muted-foreground'}`}>
                        {h.old_value !== null ? String(h.old_value) : '—'}
                      </span>
                      <span className="text-muted-foreground font-bold text-base">→</span>
                      <span className={`text-sm px-2.5 py-1 rounded-md font-medium ${h.new_value !== null ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400' : 'text-muted-foreground'}`}>
                        {h.new_value !== null ? String(h.new_value) : '—'}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Mobile Card ───────────────────────────────────────────────────────────────

function MobileStorageCard({ row, onHistory }: {
  row: DmActivity;
  onHistory: (v: { id: string; title: string }) => void;
}) {
  return (
    <div className="rounded-lg border p-3 space-y-1">
      <div className="flex items-start justify-between gap-2">
        <p className="font-medium truncate flex-1">{row.title}</p>
        <Button size="icon" variant="ghost" className="h-8 w-8 sm:h-8 sm:w-8 shrink-0" title="History"
          onClick={() => onHistory({ id: row.id, title: row.title })}>
          <History className="h-4 w-4" />
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        {format(new Date(row.date_time), 'd MMM yyyy')}
        {row.page?.name && ` · ${row.page.name}`}
      </p>
      {row.location && (
        <p className="text-xs text-muted-foreground flex items-center gap-1">
          <MapPin className="h-3 w-3" />{row.location}
        </p>
      )}
    </div>
  );
}

// ── Main Storage Page ─────────────────────────────────────────────────────────

export function StoragePage() {
  const permission = usePermission();
  const canView = permission.has(PERMISSIONS.DM_TEAM_STORAGE_VIEW);

  const [storageTab, setStorageTab] = useState<StorageTab>('all');
  const [search, setSearch] = useState('');
  const [pageFilter, setPageFilter] = useState('');
  const [page, setPage] = useState(1);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [historyActivity, setHistoryActivity] = useState<{ id: string; title: string } | null>(null);

  const { data: metaData } = useQuery({
    queryKey: ['dm-team', 'activities-meta'],
    queryFn: () => dmTeamService.getActivitiesMeta(),
  });

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['dm-team', 'storage', { storageTab, page, q: search, pageFilter, dateFrom, dateTo }],
    queryFn: () => dmTeamService.listStorage({
      page,
      limit: 20,
      q: search || undefined,
      page_id: pageFilter || undefined,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
      // Uploaded = the Activities "Upload" bucket (excludes cancelled); Cancelled = permanent cancels.
      bucket: storageTab === 'uploaded' ? 'uploaded' : undefined,
      status: storageTab === 'cancelled' ? 'cancelled' : undefined,
    }),
    enabled: !!canView,
  });

  const pagination = data?.pagination;
  const pages = metaData?.data?.pages ?? [];

  const COLUMNS: Column<DmActivity>[] = [
    {
      key: 'title',
      header: 'Title',
      cell: row => (
        <span className="flex items-center gap-2">
          <span className={`font-medium ${row.status === 'cancelled' ? 'text-muted-foreground line-through decoration-rose-400/70' : ''}`}>{row.title}</span>
          {row.status === 'cancelled' && (
            <Badge className="shrink-0 border border-rose-200 bg-rose-50 text-[10px] text-rose-700 dark:border-rose-900/50 dark:bg-rose-900/30 dark:text-rose-400">
              Cancelled
            </Badge>
          )}
        </span>
      ),
    },
    {
      key: 'upload_link',
      header: 'Uploaded Link',
      cell: row => !row.upload_link ? (
        <span className="text-muted-foreground">—</span>
      ) : !isHttpUrl(row.upload_link) ? (
        <span className="text-sm text-muted-foreground truncate max-w-[180px] block" title={row.upload_link}>{row.upload_link}</span>
      ) : (
        <a href={row.upload_link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-teal-600 hover:underline text-sm">
          <ExternalLink className="w-3.5 h-3.5 shrink-0" />
          <span className="truncate max-w-[180px]">{row.upload_link}</span>
        </a>
      ),
    },
    {
      key: 'date_time',
      header: 'Schedule Date',
      cell: row => <span className="text-sm whitespace-nowrap">{format(new Date(row.date_time), 'd MMM yyyy')}</span>,
      className: 'whitespace-nowrap',
    },
    {
      key: 'scheduled_date',
      header: 'Rescheduled Date',
      cell: row => row.scheduled_date
        ? <span className="text-sm whitespace-nowrap">{format(new Date(row.scheduled_date), 'd MMM yyyy')}</span>
        : <span className="text-muted-foreground">—</span>,
      className: 'whitespace-nowrap',
    },
    {
      key: 'page',
      header: 'Page Section',
      cell: row => <span className="text-sm">{row.page?.name ?? '—'}</span>,
    },
    {
      key: 'updated_page',
      header: 'Updated',
      cell: row => <span className="text-sm">{row.updated_page?.name ?? '—'}</span>,
    },
    {
      key: 'users_count',
      header: 'Users',
      cell: row => (
        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-teal-600 text-white text-xs font-semibold">
          {row.assigned_users.length}
        </span>
      ),
      className: 'w-16',
    },
    {
      key: 'assigned_users',
      header: 'Assigned',
      cell: row => row.assigned_users.length > 0 ? (
        <span className="text-xs">{row.assigned_users.map(u => `${u.first_name} ${u.last_name}`).join(', ')}</span>
      ) : <span className="text-muted-foreground">—</span>,
    },
    {
      key: 'location',
      header: 'Location',
      cell: row => row.location ? (
        <span className="flex items-center gap-1 text-sm">
          <MapPin className="w-3 h-3 text-purple-500 shrink-0" />
          <span className="truncate max-w-[120px]">{row.location}</span>
        </span>
      ) : <span className="text-muted-foreground">—</span>,
    },
    {
      key: 'video_stats',
      header: 'Video Stats',
      cell: row => row.video_stats && Object.keys(row.video_stats).length > 0
        ? <span className="text-xs text-teal-700">{Object.entries(row.video_stats).map(([k, v]) => `${k}: ${v}`).join(', ')}</span>
        : <span className="text-muted-foreground">—</span>,
    },
    {
      key: 'actions',
      header: '',
      cell: row => (
        <div className="flex gap-0.5">
          <Button variant="ghost" size="icon" className="h-8 w-8 sm:h-8 sm:w-8" title="History"
            onClick={() => setHistoryActivity({ id: row.id, title: row.title })}>
            <History className="h-4 w-4" />
          </Button>
        </div>
      ),
      className: 'w-10',
    },
  ];

  if (!canView) {
    return (
      <div className="p-6 text-center text-muted-foreground">
        You don't have permission to view storage.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Storage</h1>
        <p className="text-sm text-muted-foreground mt-0.5">Uploaded, archived and cancelled DM activities</p>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <Tabs
          tabs={[
            { key: 'all',       label: 'All' },
            { key: 'uploaded',  label: 'Uploaded' },
            { key: 'cancelled', label: 'Cancelled' },
          ]}
          active={storageTab}
          onChange={v => { setStorageTab(v as StorageTab); setPage(1); }}
        />
        <Input
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search…"
          className="max-w-xs"
        />
        <DateRangePicker
          from={dateFrom}
          to={dateTo}
          onChange={(f, t) => { setDateFrom(f); setDateTo(t); setPage(1); }}
          placeholder="Filter by date range"
          className="w-52"
        />
        <Select value={pageFilter || 'all'} onValueChange={v => { setPageFilter(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="All Page Sections" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Page Sections</SelectItem>
            {pages.map((p: any) => (
              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="hidden md:block">
        <DataTable
          columns={COLUMNS}
          rows={data?.data ?? []}
          rowKey={r => r.id}
          loading={isLoading}
          error={isError}
          onRetry={refetch}
          empty={<p className="text-sm text-muted-foreground py-6 text-center">{STORAGE_EMPTY_TEXT[storageTab]}</p>}
          pagination={pagination ? {
            page: pagination.page,
            totalPages: pagination.totalPages,
            total: pagination.total,
            onPageChange: setPage,
          } : undefined}
        />
      </div>
      <div className="block md:hidden space-y-2">
        {isLoading ? (
          <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>
        ) : (data?.data ?? []).length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">{STORAGE_EMPTY_TEXT[storageTab]}</p>
        ) : (data?.data ?? []).map(row => (
          <MobileStorageCard key={row.id} row={row} onHistory={setHistoryActivity} />
        ))}
      </div>

      {historyActivity && (
        <ActivityHistoryDialog
          activityId={historyActivity.id}
          title={historyActivity.title}
          open={!!historyActivity}
          onClose={() => setHistoryActivity(null)}
        />
      )}

    </div>
  );
}
